import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, MapPinIcon, ZapIcon, TrendingUpIcon, BarChart3Icon, UsersIcon, MessageSquareIcon, PhoneIcon, GlobeIcon, CheckCircleIcon, MousePointerClickIcon } from 'lucide-react';
import { ScrollReveal } from '../../components/ScrollReveal';
import { SEOHead } from '../../components/SEOHead';
const faqs = [{
  question: 'How did Viridia help Finish Coat Painting increase revenue?',
  answer: 'Using the Never Miss a Lead package, Viridia implemented AI-powered messaging to handle inbound Yelp conversations instantly. In 30 days, the AI booked 29 estimates, generating $15.05k in closed-won revenue from end-to-end AI bookings alone - not including revenue from phone calls or website leads.'
}, {
  question: 'Can AI follow-up actually close jobs for contractors?',
  answer: 'Yes. The Finish Coat Painting case study proves that AI follow-up does more than just respond fast - it helps close real jobs. By engaging high-intent local demand immediately, AI turns inquiries into booked estimates and closed revenue.'
}];
export function FinishCoatPaintingPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-758">
      <SEOHead title="Finish Coat Painting Case Study | $15.05k AI Revenue | Viridia Analytics" description="See how Finish Coat Painting used Viridia Analytics' Never Miss a Lead package to get 29 AI-booked estimates and $15.05k in closed-won revenue in 30 days." canonical="/case-studies/finish-coat-painting" keywords="painting marketing case study, Yelp ads for painters, AI lead conversion, Finish Coat Painting" faq={faqs} data-id="element-759" />

      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-760">
        <ScrollReveal data-id="element-761">
          <div className="mb-6" data-id="element-762">
            <Link to="/case-studies" className="inline-flex items-center text-gray-400 hover:text-white transition-colors text-sm font-medium" data-id="element-763">
              <ArrowRightIcon className="w-4 h-4 mr-2 rotate-180" data-id="element-764" />
              Back to Case Studies
            </Link>
          </div>

          <div className="bg-viridia-surface border border-gray-800 rounded-2xl overflow-hidden shadow-2xl" data-id="element-765">
            {/* Header / Snapshot */}
            <div className="bg-gray-900 p-8 lg:p-12 relative overflow-hidden border-b border-gray-800" data-id="element-766">
              <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-[100px]" data-id="element-767"></div>
              <div className="relative z-10" data-id="element-768">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-500/10 text-orange-400 text-xs font-bold tracking-wide uppercase mb-6 border border-orange-500/20" data-id="element-769">
                  Painting Success Story
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight max-w-4xl" data-id="element-770">
                  Finish Coat Painting: 29 AI-Booked Estimates and $15.05k in
                  Closed Revenue in 30 Days
                </h1>

                {/* Snapshot Pills */}
                <div className="flex flex-wrap gap-3 mb-10" data-id="element-771">
                  <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-viridia-black border border-gray-700 text-gray-300 text-sm" data-id="element-772">
                    <MapPinIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-773" />
                    Residential/Commercial Painting
                  </div>
                  <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-viridia-black border border-gray-700 text-gray-300 text-sm" data-id="element-774">
                    <ZapIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-775" />
                    Never Miss a Lead Package
                  </div>
                </div>

                {/* Hero Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6" data-id="element-776">
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-777">
                    <p className="text-3xl sm:text-4xl font-bold text-orange-500 mb-2" data-id="element-778">
                      29
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-779">
                      AI-Booked Estimates
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-780">
                    <p className="text-3xl sm:text-4xl font-bold text-orange-500 mb-2" data-id="element-781">
                      $15.05k
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-782">
                      Closed-Won Revenue
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-783">
                    <p className="text-3xl sm:text-4xl font-bold text-orange-500 mb-2" data-id="element-784">
                      138
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-785">
                      Total Leads
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-786">
                    <p className="text-3xl sm:text-4xl font-bold text-orange-500 mb-2" data-id="element-787">
                      64%
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-788">
                      Lead Growth
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Challenge / Solution */}
            <div className="p-8 lg:p-12 bg-viridia-black border-b border-gray-800" data-id="element-789">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12" data-id="element-790">
                <div data-id="element-791">
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-6" data-id="element-792">
                    <TrendingUpIcon className="w-6 h-6 text-red-500" data-id="element-793" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-794">
                    The Challenge
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-795">
                    Finish Coat Painting was generating Yelp demand but couldn't
                    keep up with inbound conversations. Leads were slipping
                    through the cracks because the team was busy on job sites,
                    and follow-up was inconsistent.
                  </p>
                </div>
                <div data-id="element-796">
                  <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-6" data-id="element-797">
                    <BarChart3Icon className="w-6 h-6 text-orange-500" data-id="element-798" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-799">
                    The Viridia Solution
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-800">
                    We deployed the Never Miss a Lead package - Yelp Ads
                    management with GEO targeting, AI-powered messaging to
                    handle inbound conversations instantly, and automated
                    estimate booking so every inquiry gets a response in
                    seconds.
                  </p>
                </div>
              </div>
            </div>

            {/* AI Bookings & Revenue */}
            <div className="grid grid-cols-1 md:grid-cols-2 border-b border-gray-800" data-id="element-801">
              <div className="p-8 lg:p-12 bg-viridia-surface border-r border-gray-800" data-id="element-802">
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-6" data-id="element-803">
                  <ZapIcon className="w-6 h-6 text-orange-500" data-id="element-804" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-805">
                  29 AI-Booked Estimates
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6" data-id="element-806">
                  In 30 days, the AI booked 29 estimates (roughly one per day).
                  One booking actually represented 2 separate guys getting
                  quotes. These were{' '}
                  <strong className="text-white" data-id="element-807">AI-worked leads</strong>, not
                  random CRM leads. The AI handled the conversation, qualified
                  the prospect, and booked the estimate.
                </p>
              </div>
              <div className="p-8 lg:p-12 bg-viridia-surface" data-id="element-808">
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-6" data-id="element-809">
                  <UsersIcon className="w-6 h-6 text-orange-500" data-id="element-810" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-811">
                  $15.05k Closed-Won Revenue
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6" data-id="element-812">
                  The 29 AI-booked estimates resulted in $15.05k in closed-won
                  revenue. This revenue came{' '}
                  <strong className="text-white" data-id="element-813">
                    from end-to-end AI bookings alone
                  </strong>{' '}
                  - it does NOT include revenue from phone calls or website
                  leads. This proves that AI follow-up doesn't just respond fast
                  - it helps close real jobs.
                </p>
              </div>
            </div>

            {/* 30-Day Performance Breakdown */}
            <div className="p-8 lg:p-12 bg-viridia-black border-b border-gray-800" data-id="element-814">
              <h3 className="text-2xl font-bold text-white mb-8" data-id="element-815">
                30-Day Performance
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-816">
                <div className="space-y-6" data-id="element-817">
                  <div className="bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-818">
                    <div className="flex justify-between items-center mb-2" data-id="element-819">
                      <span className="text-gray-400" data-id="element-820">Impressions</span>
                      <span className="text-white font-bold" data-id="element-821">33.2k</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-2" data-id="element-822">
                      <div className="bg-orange-500 h-2 rounded-full" style={{
                      width: '89%'
                    }} data-id="element-823"></div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-824">
                      29.4k (89%) from Yelp Ads
                    </p>
                  </div>
                  <div className="bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-825">
                    <div className="flex justify-between items-center mb-2" data-id="element-826">
                      <span className="text-gray-400" data-id="element-827">Page Visits</span>
                      <span className="text-white font-bold" data-id="element-828">370</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-2" data-id="element-829">
                      <div className="bg-orange-500 h-2 rounded-full" style={{
                      width: '79%'
                    }} data-id="element-830"></div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-831">
                      293 (79%) from Yelp Ads
                    </p>
                  </div>
                  <div className="bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-832">
                    <div className="flex justify-between items-center mb-2" data-id="element-833">
                      <span className="text-gray-400" data-id="element-834">Total Leads</span>
                      <span className="text-white font-bold" data-id="element-835">138</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-2" data-id="element-836">
                      <div className="bg-orange-500 h-2 rounded-full" style={{
                      width: '59%'
                    }} data-id="element-837"></div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-838">
                      81 (59%) from Yelp Ads
                    </p>
                  </div>
                </div>

                <div data-id="element-839">
                  <h4 className="text-lg font-semibold text-white mb-4" data-id="element-840">
                    Lead Mix
                  </h4>
                  <div className="bg-viridia-surface rounded-xl border border-gray-800 overflow-hidden" data-id="element-841">
                    <table className="w-full text-left text-sm" data-id="element-842">
                      <thead className="bg-gray-900 border-b border-gray-800" data-id="element-843">
                        <tr data-id="element-844">
                          <th className="p-4 text-gray-400 font-medium" data-id="element-845">
                            Lead Type
                          </th>
                          <th className="p-4 text-gray-400 font-medium" data-id="element-846">
                            Count
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800" data-id="element-847">
                        <tr data-id="element-848">
                          <td className="p-4 text-white flex items-center" data-id="element-849">
                            <PhoneIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-850" />{' '}
                            Calls
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-851">52</td>
                        </tr>
                        <tr data-id="element-852">
                          <td className="p-4 text-white flex items-center" data-id="element-853">
                            <MessageSquareIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-854" />{' '}
                            Messages
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-855">50</td>
                        </tr>
                        <tr data-id="element-856">
                          <td className="p-4 text-white flex items-center" data-id="element-857">
                            <GlobeIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-858" />{' '}
                            Website Visits
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-859">33</td>
                        </tr>
                        <tr data-id="element-860">
                          <td className="p-4 text-white flex items-center" data-id="element-861">
                            <MousePointerClickIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-862" />{' '}
                            CTA Clicks
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-863">3</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Local Visibility */}
            <div className="p-8 lg:p-12 bg-viridia-surface" data-id="element-864">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-6" data-id="element-865">
                <MapPinIcon className="w-6 h-6 text-orange-500" data-id="element-866" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4" data-id="element-867">
                GEO & Local Painting Demand
              </h3>
              <ul className="space-y-4 mb-8" data-id="element-868">
                <li className="flex items-start" data-id="element-869">
                  <CheckCircleIcon className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-870" />
                  <span className="text-gray-300" data-id="element-871">
                    Finish Coat received more page views than similar businesses
                    on Yelp.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-872">
                  <CheckCircleIcon className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-873" />
                  <span className="text-gray-300" data-id="element-874">
                    Captured rising local search trends for 'Painters Interior',
                    'House Painters', and 'Interior Painting'.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.2} data-id="element-875">
          <div className="mt-12 text-center" data-id="element-876">
            <h3 className="text-2xl font-bold text-white mb-6" data-id="element-877">
              Want to see what's leaking leads in your pipeline?
            </h3>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-viridia-black bg-white rounded-lg hover:bg-gray-200 transition-all duration-300" data-id="element-878">
              Book a Strategy Call
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-879" />
            </Link>
          </div>
        </ScrollReveal>
      </section>
    </div>;
}